"use client";

import { useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

/* ------------------------------------------------------------------ */
/*  Regex constants (module level per Biome rule)                     */
/* ------------------------------------------------------------------ */

const RE_SENTENCE_SPLIT = /(?<=[.!?])\s+/;
const RE_SPLIT_WORDS = /\s+/;
const RE_LOWERCASE_LETTER = /[a-z]/;

/* ------------------------------------------------------------------ */
/*  Transition word dictionary                                        */
/* ------------------------------------------------------------------ */

const TRANSITION_CATEGORIES: Record<string, string[]> = {
  Addition: [
    "additionally",
    "also",
    "and",
    "besides",
    "coupled with",
    "furthermore",
    "in addition",
    "likewise",
    "moreover",
    "not only",
    "similarly",
    "what is more",
  ],
  Contrast: [
    "although",
    "but",
    "conversely",
    "despite",
    "even though",
    "however",
    "in contrast",
    "instead",
    "nevertheless",
    "nonetheless",
    "on the contrary",
    "on the other hand",
    "rather",
    "still",
    "whereas",
    "while",
    "yet",
  ],
  "Cause & Effect": [
    "accordingly",
    "as a result",
    "because",
    "consequently",
    "due to",
    "for this reason",
    "hence",
    "since",
    "so",
    "therefore",
    "thus",
  ],
  Sequence: [
    "after",
    "afterward",
    "before",
    "eventually",
    "finally",
    "first",
    "firstly",
    "initially",
    "lastly",
    "meanwhile",
    "next",
    "previously",
    "second",
    "secondly",
    "subsequently",
    "then",
    "third",
    "thirdly",
  ],
  Example: [
    "for example",
    "for instance",
    "in particular",
    "namely",
    "specifically",
    "such as",
    "to illustrate",
  ],
  Summary: [
    "in conclusion",
    "in short",
    "in summary",
    "overall",
    "to conclude",
    "to summarize",
    "to sum up",
    "ultimately",
  ],
};

/* ------------------------------------------------------------------ */
/*  Analysis types and logic                                          */
/* ------------------------------------------------------------------ */

interface FoundTransition {
  word: string;
  category: string;
  sentence: string;
}

interface AnalysisResult {
  wordCount: number;
  sentenceCount: number;
  transitions: FoundTransition[];
  categoryCounts: Record<string, number>;
  sentencesWithTransitions: number;
  transitionPercent: number;
}

function isWordBoundary(
  sentenceLower: string,
  idx: number,
  wordLength: number
): boolean {
  const charBefore = idx > 0 ? sentenceLower[idx - 1] : " ";
  const charAfter =
    idx + wordLength < sentenceLower.length
      ? sentenceLower[idx + wordLength]
      : " ";
  return !(
    RE_LOWERCASE_LETTER.test(charBefore) || RE_LOWERCASE_LETTER.test(charAfter)
  );
}

function findTransitionsInSentence(
  sentenceLower: string,
  category: string,
  words: string[]
): { word: string; category: string; index: number }[] {
  const found: { word: string; category: string; index: number }[] = [];
  for (const word of words) {
    const wordLower = word.toLowerCase();
    let idx = sentenceLower.indexOf(wordLower);
    while (idx !== -1) {
      if (isWordBoundary(sentenceLower, idx, wordLower.length)) {
        found.push({ word, category, index: idx });
      }
      idx = sentenceLower.indexOf(wordLower, idx + wordLower.length);
    }
  }
  return found;
}

function initCategoryCounts(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const category of Object.keys(TRANSITION_CATEGORIES)) {
    counts[category] = 0;
  }
  return counts;
}

export function analyzeTransitions(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const sentences = trimmed
    .split(RE_SENTENCE_SPLIT)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const wordCount = trimmed.split(RE_SPLIT_WORDS).filter(Boolean).length;
  const sentenceCount = Math.max(sentences.length, 1);
  const transitions: FoundTransition[] = [];
  const categoryCounts = initCategoryCounts();
  const sentencesWithTransitionSet = new Set<number>();

  for (let i = 0; i < sentences.length; i++) {
    const sentenceLower = sentences[i].toLowerCase();

    const matches = Object.entries(TRANSITION_CATEGORIES)
      .flatMap(([category, words]) =>
        findTransitionsInSentence(sentenceLower, category, words)
      )
      .sort((a, b) => a.index - b.index);
    for (const match of matches) {
      transitions.push({
        word: match.word,
        category: match.category,
        sentence: sentences[i],
      });
      categoryCounts[match.category]++;
      sentencesWithTransitionSet.add(i);
    }
  }

  const sentencesWithTransitions = sentencesWithTransitionSet.size;
  const transitionPercent = (sentencesWithTransitions / sentenceCount) * 100;

  return {
    wordCount,
    sentenceCount,
    transitions,
    categoryCounts,
    sentencesWithTransitions,
    transitionPercent,
  };
}

/** Which `categoryNames` key labels each dictionary category. */
const CATEGORY_NAME_KEYS: Record<
  string,
  keyof typeof TRANSITION_WORD_FINDER_STRINGS.categoryNames
> = {
  Addition: "addition",
  Contrast: "contrast",
  "Cause & Effect": "causeAndEffect",
  Sequence: "sequence",
  Example: "example",
  Summary: "summary",
};

/* ------------------------------------------------------------------ */
/*  Category colors                                                   */
/* ------------------------------------------------------------------ */

const CATEGORY_COLORS: Record<string, string> = {
  Addition: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  Contrast:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  "Cause & Effect":
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  Sequence:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  Example: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300",
  Summary:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
};

/* ------------------------------------------------------------------ */
/*  English strings. A translated content file overrides these        */
/*  through `ui`.                                                     */
/* ------------------------------------------------------------------ */

export const TRANSITION_WORD_FINDER_STRINGS = {
  inputAriaLabel: "Text to find transition words in",
  inputPlaceholder: "Paste or type your text here to find transition words...",
  totalWords: "Total Words",
  sentences: "Sentences",
  transitionsFound: "Transitions Found",
  sentencesWithTransitions: "Sentences w/ Transitions",
  rating: "Rating",
  ratingGoodBalance: "Good Balance",
  ratingGoodBalanceAdvice:
    "Your use of transition words creates a well-connected, flowing text.",
  ratingCouldImprove: "Could Improve",
  ratingCouldImproveAdvice:
    "Consider adding more transition words to improve the flow between ideas.",
  ratingNeedsTransitions: "Needs Transitions",
  ratingNeedsTransitionsAdvice:
    "Your text could benefit from more transition words to connect ideas and guide readers through your argument.",
  ratingOverused: "Overused",
  ratingOverusedAdvice:
    "You may be overusing transitions. Try removing some to avoid a formulaic tone.",
  categoryBreakdown: "Category Breakdown",
  transitionsFoundHeading: "Transitions Found",
  noTransitionsFound: "No transition words found.",
  noTransitionsAdvice:
    'Consider adding transitions like "however," "therefore," or "furthermore" to improve flow.',
  categoryNames: {
    addition: "Addition",
    contrast: "Contrast",
    causeAndEffect: "Cause & Effect",
    sequence: "Sequence",
    example: "Example",
    summary: "Summary",
  },
} as const;

export function getRating(
  percent: number,
  strings: typeof TRANSITION_WORD_FINDER_STRINGS
): {
  label: string;
  color: string;
  advice: string;
} {
  if (percent > 45) {
    return {
      label: strings.ratingOverused,
      color:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      advice: strings.ratingOverusedAdvice,
    };
  }
  if (percent >= 25) {
    return {
      label: strings.ratingGoodBalance,
      color:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      advice: strings.ratingGoodBalanceAdvice,
    };
  }
  if (percent >= 15) {
    return {
      label: strings.ratingCouldImprove,
      color:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      advice: strings.ratingCouldImproveAdvice,
    };
  }
  return {
    label: strings.ratingNeedsTransitions,
    color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    advice: strings.ratingNeedsTransitionsAdvice,
  };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Widget Component                                                  */
/* ------------------------------------------------------------------ */

export default function TransitionWordFinderWidget() {
  const strings = useToolUi(TRANSITION_WORD_FINDER_STRINGS);
  const fmt = useToolNumber();

  function categoryName(category: string): string {
    const key = CATEGORY_NAME_KEYS[category];
    return key ? strings.categoryNames[key] : category;
  }
  const [text, setText] = useState("");

  const analysis = useMemo(() => analyzeTransitions(text), [text]);
  const rating = analysis
    ? getRating(analysis.transitionPercent, strings)
    : null;

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.inputAriaLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.inputPlaceholder}
        value={text}
      />

      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.sentences}
            value={fmt(analysis.sentenceCount)}
          />
          <StatCard
            label={strings.transitionsFound}
            value={fmt(analysis.transitions.length)}
          />
          <StatCard
            label={strings.sentencesWithTransitions}
            value={`${fmt(analysis.transitionPercent, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
          />
        </div>
      )}

      {/* Rating */}
      {analysis && rating && (
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="mb-2 flex items-center gap-3">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.rating}
            </h2>
            <span
              className={`rounded-full px-2.5 py-0.5 font-medium text-xs ${rating.color}`}
            >
              {rating.label}
            </span>
          </div>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {rating.advice}
          </p>
        </div>
      )}

      {/* Category breakdown */}
      {analysis && analysis.transitions.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.categoryBreakdown}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(analysis.categoryCounts).map(
              ([category, count]) => (
                <div
                  className="flex items-center justify-between rounded-lg border border-border bg-card p-4"
                  key={category}
                >
                  <span
                    className={`rounded-full px-2.5 py-0.5 font-medium text-xs ${CATEGORY_COLORS[category] || "bg-gray-100 text-gray-800"}`}
                  >
                    {categoryName(category)}
                  </span>
                  <span className="font-semibold text-foreground">
                    {fmt(count)}
                  </span>
                </div>
              )
            )}
          </div>
        </section>
      )}

      {/* Transitions list */}
      {analysis && analysis.transitions.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.transitionsFoundHeading}
          </h2>
          <div className="space-y-3">
            {analysis.transitions.map((item, i) => (
              <div
                className="rounded-lg border border-border bg-card p-4"
                key={`trans-${i}-${item.word}`}
              >
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-medium text-foreground text-sm">
                    &quot;{item.word}&quot;
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${CATEGORY_COLORS[item.category] || "bg-gray-100 text-gray-800"}`}
                  >
                    {categoryName(item.category)}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {item.sentence}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* No transitions found */}
      {analysis && analysis.transitions.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noTransitionsFound}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.noTransitionsAdvice}
          </p>
        </div>
      )}
    </>
  );
}
